import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { api, API, fmtUSD } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Search, ExternalLink, Download, Upload, FileCheck2, FileEdit } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CTADocumentImport from "../components/CTADocumentImport";
import CTARescanDiff from "../components/CTARescanDiff";
import BulkCTAImport from "../components/BulkCTAImport";

const emptyGrant = () => ({ _k: Math.random().toString(36).slice(2), visit_name: "", visit_code: "", grant_amount: 0, frequency: "per patient" });
const emptyPT = () => ({ name: "", amount: 0, description: "" });
const emptySF = () => ({ name: "", amount: 0 });

export default function CTALibrary() {
  const [ctas, setCtas] = useState([]);
  const [sites, setSites] = useState([]);
  const [coverages, setCoverages] = useState({});
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("signed"); // signed | draft | all
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    study_name: "", protocol_number: "", sponsor: "", cro: "",
    therapeutic_area: "", phase: "Phase II", site_id: "", pi_name: "",
    total_budget: 0, payment_model: "Per-Visit",
    pi_split_percent: 0, clinic_fee_percent: 0,
    payment_terms: "Net-30", payment_trigger: "invoice submit",
    contract_execution_date: "", study_start_date: "",
    visit_grants: [emptyGrant()], pass_throughs: [], startup_fees: [],
  });
  const nav = useNavigate();

  const load = async () => {
    const [c, s] = await Promise.all([
      api.get("/ctas").then((r) => r.data),
      api.get("/sites").then((r) => r.data),
    ]);
    setCtas(c); setSites(s);
    // Fetch coverage in parallel for visible rows
    const cov = await Promise.all(c.map((x) => api.get(`/ctas/${x.id}/coverage`).then((r) => [x.id, r.data]).catch(() => [x.id, null])));
    const map = {};
    cov.forEach(([id, v]) => { if (v) map[id] = v; });
    setCoverages(map);
  };
  useEffect(() => { load(); }, []);

  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";

  const filtered = useMemo(() => {
    const qq = q.toLowerCase();
    return ctas.filter((c) => {
      // Status filter — undefined/null status treated as "signed" for backward compat
      const st = c.status || "signed";
      if (statusFilter !== "all" && st !== statusFilter) return false;
      return (
        !qq ||
        c.study_name.toLowerCase().includes(qq) ||
        c.sponsor.toLowerCase().includes(qq) ||
        c.therapeutic_area.toLowerCase().includes(qq) ||
        c.protocol_number.toLowerCase().includes(qq)
      );
    });
  }, [ctas, q, statusFilter]);

  const counts = useMemo(() => {
    let signed = 0, draft = 0;
    ctas.forEach((c) => { ((c.status || "signed") === "draft") ? draft++ : signed++; });
    return { signed, draft, all: ctas.length };
  }, [ctas]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/ctas/${id}/status`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const createCTA = async () => {
    if (!form.study_name || !form.sponsor || !form.site_id) {
      toast.error("Fill Study Name, Sponsor and Site");
      return;
    }
    await api.post("/ctas", {
      ...form,
      total_budget: Number(form.total_budget),
      pi_split_percent: Number(form.pi_split_percent),
      clinic_fee_percent: Number(form.clinic_fee_percent),
      visit_grants: form.visit_grants.filter((v) => v.visit_code).map(({ _k, ...rest }) => rest),
    });
    toast.success("CTA created");
    setOpen(false);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this CTA?")) return;
    await api.delete(`/ctas/${id}`);
    load();
  };

  const exportCtas = () => {
    window.location.href = `${API}/admin/ctas-export`;
  };

  const importCtas = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await fetch(`${API}/admin/ctas-import`, { method: "POST", body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Import failed");
      toast.success(`Imported: ${data.inserted} new, ${data.upserted} updated${data.errors?.length ? `, ${data.errors.length} errors` : ""}`);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      e.target.value = "";
    }
  };

  return (
    <AppShell
      title="CTA Library"
      subtitle="Every clinical trial agreement as a queryable historical record"
      actions={
        <div className="flex items-center gap-2">
          <CTADocumentImport sites={sites} onCreated={load} />
          <BulkCTAImport sites={sites} onCreated={load} />
          <Button variant="outline" onClick={exportCtas} data-testid="cta-export-btn"><Download className="w-4 h-4 mr-1" /> Export</Button>
          <label className="inline-flex items-center">
            <input type="file" accept=".json" hidden onChange={importCtas} data-testid="cta-import-input" />
            <Button variant="outline" asChild><span className="cursor-pointer" onClick={(e) => e.currentTarget.parentElement.querySelector("input").click()}><Upload className="w-4 h-4 mr-1" /> Import</span></Button>
          </label>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-cta-btn" className="bg-[#0A4C95] hover:bg-[#083d79]">
              <Plus className="w-4 h-4 mr-1" /> New CTA
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto">
            <DialogHeader><DialogTitle>New Clinical Trial Agreement</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div><Label>Study Name *</Label><Input data-testid="cta-study-name" value={form.study_name} onChange={(e) => setField("study_name", e.target.value)} /></div>
              <div><Label>Protocol #</Label><Input value={form.protocol_number} onChange={(e) => setField("protocol_number", e.target.value)} /></div>
              <div><Label>Sponsor *</Label><Input data-testid="cta-sponsor" value={form.sponsor} onChange={(e) => setField("sponsor", e.target.value)} /></div>
              <div><Label>CRO</Label><Input value={form.cro} onChange={(e) => setField("cro", e.target.value)} /></div>
              <div><Label>Therapeutic Area *</Label><Input value={form.therapeutic_area} onChange={(e) => setField("therapeutic_area", e.target.value)} /></div>
              <div>
                <Label>Phase</Label>
                <Select value={form.phase} onValueChange={(v) => setField("phase", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Phase I","Phase II","Phase III","Phase IV"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Site *</Label>
                <Select value={form.site_id} onValueChange={(v) => setField("site_id", v)}>
                  <SelectTrigger data-testid="cta-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                  <SelectContent>
                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>PI Name</Label><Input value={form.pi_name} onChange={(e) => setField("pi_name", e.target.value)} /></div>
              <div><Label>Total Budget ($)</Label><Input type="number" value={form.total_budget} onChange={(e) => setField("total_budget", e.target.value)} /></div>
              <div>
                <Label>Payment Model</Label>
                <Select value={form.payment_model} onValueChange={(v) => setField("payment_model", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Per-Visit","Flat Rate","Per-Procedure","Milestone","Hybrid"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>PI Split %</Label><Input type="number" value={form.pi_split_percent} onChange={(e) => setField("pi_split_percent", e.target.value)} /></div>
              <div><Label>Clinic Fee %</Label><Input type="number" value={form.clinic_fee_percent} onChange={(e) => setField("clinic_fee_percent", e.target.value)} /></div>
              <div>
                <Label>Payment Terms</Label>
                <Select value={form.payment_terms} onValueChange={(v) => setField("payment_terms", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Net-30","Net-45","Net-60"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Payment Trigger</Label><Input value={form.payment_trigger} onChange={(e) => setField("payment_trigger", e.target.value)} /></div>
              <div><Label>Execution Date</Label><Input type="date" value={form.contract_execution_date} onChange={(e) => setField("contract_execution_date", e.target.value)} /></div>
              <div><Label>Start Date</Label><Input type="date" value={form.study_start_date} onChange={(e) => setField("study_start_date", e.target.value)} /></div>
            </div>

            <div className="mt-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Visit-level Grant Table</h4>
                <Button type="button" size="sm" variant="outline" onClick={() => setField("visit_grants", [...form.visit_grants, emptyGrant()])}><Plus className="w-3.5 h-3.5 mr-1" /> Add Visit</Button>
              </div>
              <div className="mt-2 space-y-2">
                {form.visit_grants.map((v, i) => (
                  <div key={v._k || i} className="grid grid-cols-12 gap-2">
                    <Input className="col-span-4" placeholder="Visit Name" value={v.visit_name} onChange={(e) => { const a=[...form.visit_grants]; a[i].visit_name = e.target.value; setField("visit_grants", a); }} />
                    <Input className="col-span-2" placeholder="Code" value={v.visit_code} onChange={(e) => { const a=[...form.visit_grants]; a[i].visit_code = e.target.value; setField("visit_grants", a); }} />
                    <Input className="col-span-2" type="number" placeholder="$ Grant" value={v.grant_amount} onChange={(e) => { const a=[...form.visit_grants]; a[i].grant_amount = Number(e.target.value); setField("visit_grants", a); }} />
                    <Select value={v.frequency} onValueChange={(val) => { const a=[...form.visit_grants]; a[i].frequency = val; setField("visit_grants", a); }}>
                      <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["per patient","per cycle","once"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" className="col-span-1" onClick={() => setField("visit_grants", form.visit_grants.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={createCTA} data-testid="cta-save-btn" className="bg-[#0A4C95] hover:bg-[#083d79]">Save CTA</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      }
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="cta-search" className="pl-9" placeholder="Search study, sponsor, therapeutic area..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="inline-flex border border-slate-200 rounded-md p-0.5 bg-slate-50">
          {[
            { k: "signed", label: "Signed", icon: FileCheck2, count: counts.signed },
            { k: "draft",  label: "Drafts", icon: FileEdit,  count: counts.draft },
            { k: "all",    label: "All",    icon: null,       count: counts.all },
          ].map(({ k, label, icon: Icon, count }) => (
            <button
              key={k}
              onClick={() => setStatusFilter(k)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors ${statusFilter === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`cta-filter-${k}`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />} {label} <Badge variant="outline" className="bg-slate-100 text-[10px] h-4 px-1.5 ml-0.5">{count}</Badge>
            </button>
          ))}
        </div>
        <div className="text-sm text-slate-500">{filtered.length} showing</div>
      </div>

      <div className="bg-white border border-border rounded-md shadow-sm overflow-auto">
        <table className="w-full text-sm stryde-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2.5">Study</th>
              <th className="text-left px-4 py-2.5">Sponsor</th>
              <th className="text-left px-4 py-2.5">Therapeutic Area</th>
              <th className="text-left px-4 py-2.5">Phase</th>
              <th className="text-left px-4 py-2.5">Site</th>
              <th className="text-right px-4 py-2.5">Total Budget</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-center px-4 py-2.5">Coverage</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-slate-500">No CTAs match this filter.</td></tr>
            )}
            {filtered.map((c) => {
              const cov = coverages[c.id];
              const bandClass = cov?.band === "excellent" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : cov?.band === "good" ? "bg-blue-50 text-blue-800 border-blue-200"
                : cov?.band === "fair" ? "bg-amber-100 text-amber-800 border-amber-200"
                : "bg-red-100 text-red-800 border-red-200";
              return (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cta-row-${c.id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-semibold text-slate-900">{c.study_name}</div>
                  <div className="text-xs text-slate-500">{c.protocol_number}</div>
                </td>
                <td className="px-4 py-2.5 text-slate-700">{c.sponsor}</td>
                <td className="px-4 py-2.5"><Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">{c.therapeutic_area}</Badge></td>
                <td className="px-4 py-2.5">{c.phase}</td>
                <td className="px-4 py-2.5 text-slate-700">{siteName(c.site_id)}</td>
                <td className="px-4 py-2.5 text-right font-mono-fin">{fmtUSD(c.total_budget)}</td>
                <td className="px-4 py-2.5">
                  <Select value={c.status || "signed"} onValueChange={(v) => setStatus(c.id, v)}>
                    <SelectTrigger className="h-7 w-28 text-xs" data-testid={`cta-status-${c.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="signed"><span className="inline-flex items-center gap-1.5"><FileCheck2 className="w-3 h-3 text-emerald-600" /> Signed</span></SelectItem>
                      <SelectItem value="draft"><span className="inline-flex items-center gap-1.5"><FileEdit className="w-3 h-3 text-amber-600" /> Draft</span></SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-2.5 text-center">
                  {cov ? (
                    <Badge variant="outline" className={bandClass} title={`${cov.gaps.length} gaps`} data-testid={`coverage-badge-${c.id}`}>
                      <span className="font-mono-fin font-semibold">{cov.score}</span>
                      <span className="ml-1 capitalize text-[10px]">· {cov.band}</span>
                    </Badge>
                  ) : <span className="text-slate-400 text-xs">—</span>}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <CTARescanDiff cta={c} onUpdated={load} />
                  <Button size="icon" variant="ghost" onClick={() => nav(`/studies/${c.id}`)} title="Study P&L + Budget Analysis"><ExternalLink className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
